import { useCallback, useEffect, useRef, useState } from 'react';
import baseapi, { API_BASE } from '../baseapi';

/* ---------- API types (backend/app/dashboard.py) ---------- */
export type Source = 'camera' | 'none';
export type DayStatus = 'past' | 'live' | 'upcoming';

export interface Alankaram {
  day_no: number;
  label: string;          // "Day 1 (Prathama)"
  label_te: string;       // "1వ రోజు (ప్రథమ)"
  tithi: string;
  tithi_te: string;
  name: string;
  name_te: string;
  about: string;
}

export interface FestivalDayInfo extends Alankaram {
  day: string;            // D1..D9
  date: string;           // YYYY-MM-DD
  status: DayStatus;
}

export interface DashboardSnapshot {
  type: 'snapshot';
  day: string | null;
  date: string;
  status: DayStatus;
  alankaram: Alankaram | null;
  server_time: number;
  kpis: {
    total_footfall: { value: number | null; last_15m: number | null; prev_day: number | null; change: number | null; change_pct: number | null; source: Source };
    total_exited: { value: number | null; dispersal_pct: number | null; source: Source };
    in_hill: { value: number | null; in?: number | null; out?: number | null; in_15m?: number | null; out_15m?: number | null; source: Source };
    antharalayam?: { value: number | null; in: number | null; out: number | null; in_15m: number | null; out_15m: number | null; source: Source };
    cctv: { online: number | null; total: number | null; offline: number | null; online_pct: number | null; on_demand?: number; source: Source };
    efficiency: { value: number | null; source: Source };
    peak_surge: { from: string | null; to: string | null; rate_per_hr: number | null; source: Source };
    holdings_load: { value: number | null; hubs: number; source: Source };
  };
  queue_stages: { id: string; name: string; count: number | null; last_15m: number | null; capacity: number | null; wait_min: number | null; source: Source;
                  exited?: number | null; exited_15m?: number | null; in_zone?: number | null; in_view?: number | null; cameras_now?: number;
                  peak_hour?: string | null; peak_count?: number | null }[];
  hourly: { hour: string; inflow: number | null; outflow: number | null; darshan_rate: number | null }[];
  holdings: { id: string; name: string; value: number | null; capacity?: number | null; boxes_counted?: number | null; source: Source }[];
  feeder_share: { id: string; name: string; value: number | null; pct: number | null; source: Source }[];
  prasadam: { series: unknown[]; source: Source };
  darshan_lines: {
    lines?: { id: string; name: string; color: string | null; value: number | null; last_15m: number | null; pct: number | null; source: Source }[];
    split: Record<string, number> | null; series: unknown[]; total?: number | null; source: Source;
  };
  choke_points: {
    items: { id: string; name: string; area: string | null; people_now: number | null; last_15m: number | null; passed: number;
             level: 'normal' | 'watch' | 'critical' | null; state: string | null }[];
    watch?: number; critical?: number; source: Source;
  };
  staff: { items: unknown[]; source: Source };
  vehicle_crossings: Record<string, number | string>;
  checkpoints: { id: string; name: string; people: { toward: number; away: number }; vehicles: { toward: number; away: number } }[];
  cameras: { id: string; checkpoint: string; name: string; ip: string; state: string }[] | null;
}

type WsMessage =
  | DashboardSnapshot
  | ({ type: 'update'; day: string | null; date: string; server_time: number } & Partial<Omit<DashboardSnapshot, 'type'>>)
  | { type: 'day_changed'; day: string | null; date: string }
  | { type: 'pong'; server_time: number };

/** 'TODAY' = the current date even outside the festival (live), otherwise a festival day label D1..D9. */
export const TODAY = 'TODAY';
export type Connection = 'live' | 'connecting' | 'reconnecting' | 'report' | 'error';

function dashboardWsUrl(): string {
  const base = new URL(API_BASE, window.location.href);
  return `${base.protocol === 'https:' ? 'wss:' : 'ws:'}//${base.host}/ws/dashboard`;
}

export function useFestivalDashboard() {
  const [days, setDays] = useState<FestivalDayInfo[]>([]);
  const [today, setToday] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [data, setData] = useState<DashboardSnapshot | null>(null);
  const [connection, setConnection] = useState<Connection>('connecting');
  const [error, setError] = useState<string | null>(null);
  const selectedRef = useRef<string | null>(null);
  selectedRef.current = selected;

  const loadDays = useCallback(async () => {
    try {
      const res = await baseapi.get('/dashboard/days');
      const raw = res?.data;
      const daysList: FestivalDayInfo[] = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.days)
          ? raw.days
          : Array.isArray(raw?.data)
            ? raw.data
            : [];
      const todayVal: string = (typeof raw?.today === 'string' ? raw.today : null) ?? new Date().toISOString().slice(0, 10);
      setDays(daysList);
      setToday(todayVal);
      return { days: daysList, today: todayVal };
    } catch (err) {
      setDays([]);
      throw err;
    }
  }, []);

  // festival calendar; first load picks the live festival day, else today
  useEffect(() => {
    let alive = true;
    loadDays()
      .then(d => {
        if (alive && selectedRef.current === null) {
          const liveDay = d?.days?.find(x => x?.status === 'live')?.day;
          setSelected(liveDay ?? TODAY);
        }
      })
      .catch(() => {
        if (alive) {
          setError('Cannot reach the dashboard API');
          if (selectedRef.current === null) {
            setSelected(TODAY);
          }
        }
      });
    const t = window.setInterval(() => { loadDays().catch(() => undefined); }, 5 * 60 * 1000);
    return () => { alive = false; window.clearInterval(t); };
  }, [loadDays]);

  const isLive = selected === TODAY || Boolean(days?.find?.(d => d?.day === selected)?.status === 'live');

  // live day: WebSocket (snapshot on connect, then partial updates, day_changed at midnight)
  useEffect(() => {
    if (!selected || !isLive) return;
    let ws: WebSocket | null = null;
    let retry = 0;
    let closed = false;
    let retryTimer: number | undefined;
    let pingTimer: number | undefined;

    const connect = () => {
      setConnection(retry === 0 ? 'connecting' : 'reconnecting');
      ws = new WebSocket(dashboardWsUrl());
      ws.onopen = () => {
        retry = 0;
        setConnection('live');
        setError(null);
        pingTimer = window.setInterval(() => ws?.readyState === WebSocket.OPEN && ws.send('ping'), 25000);
      };
      ws.onmessage = (ev) => {
        const msg = JSON.parse(ev.data) as WsMessage;
        if (msg.type === 'snapshot') {
          setData(msg);
        } else if (msg.type === 'update') {
          const { type: _t, ...changed } = msg;
          setData(prev => (prev && prev.date === msg.date ? { ...prev, ...changed } : prev));
        } else if (msg.type === 'day_changed') {
          loadDays().then(() => {
            // follow the live day across midnight
            if (selectedRef.current !== TODAY) setSelected(msg.day ?? TODAY);
          }).catch(() => undefined);
        }
      };
      ws.onclose = () => {
        window.clearInterval(pingTimer);
        if (closed) return;
        retry += 1;
        setConnection('reconnecting');
        retryTimer = window.setTimeout(connect, Math.min(30000, 1000 * 2 ** Math.min(retry, 5)));
      };
      ws.onerror = () => ws?.close();
    };

    setData(null);
    connect();
    return () => {
      closed = true;
      window.clearTimeout(retryTimer);
      window.clearInterval(pingTimer);
      ws?.close();
    };
  }, [selected, isLive, loadDays]);

  // past / upcoming day: one REST call
  useEffect(() => {
    if (!selected || isLive) return;
    let alive = true;
    setData(null);
    setConnection('connecting');
    baseapi.get(`/dashboard/days/${encodeURIComponent(selected)}`)
      .then(res => { if (alive) { setData(res.data); setConnection('report'); setError(null); } })
      .catch(() => { if (alive) { setConnection('error'); setError('Cannot load this day'); } });
    return () => { alive = false; };
  }, [selected, isLive]);

  return { days, today, selected, setSelected, data, connection, error, isLive };
}
